// Bridges backend AppliedSetting values (always strings, with a 'type' tag) to the
// settings page formatters (which expect typed values: bool, number, string).

import type { AppliedSettingModel } from '@/app/models/timesheetModel'
import { buildTimesheetBasicFormatters } from '@/modules/settings/pages/timesheet-basic/formatters'
import { buildTimesheetAdditionalFormatters } from '@/modules/settings/pages/timesheet-additional/formatters'

const toCamel = (pascal: string): string =>
  pascal.length === 0 ? pascal : pascal[0].toLowerCase() + pascal.slice(1)

const parseValue = (setting: AppliedSettingModel): unknown => {
  if (setting.type === 'bool') return setting.value === 'true'
  if (setting.type?.startsWith('enum:')) {
    const n = parseInt(setting.value, 10)
    return Number.isNaN(n) ? setting.value : n
  }
  return setting.value
}

export const buildExplainValueFormatter = (
  t: (key: string) => string,
): ((setting: AppliedSettingModel | undefined) => string) => {
  const formatters: Record<string, (v: unknown) => string> = {
    ...buildTimesheetBasicFormatters(t),
    ...buildTimesheetAdditionalFormatters(t),
  }

  return (setting) => {
    if (!setting) return '—'
    const camelKey = toCamel(setting.key)
    const formatter = formatters[camelKey]
    if (formatter) {
      try {
        return formatter(parseValue(setting))
      } catch {
        // Fall through to defaults below.
      }
    }
    if (setting.type === 'bool') {
      return setting.value === 'true' ? t('common.yes') : t('common.no')
    }
    return setting.value
  }
}
